import { addDays, format, addMinutes, isBefore, startOfDay } from 'date-fns';

import { generalDate } from '~/ui/constants/dateFormat';

export const isoToFormat = (date: string, dateFormat = generalDate): string =>
  format(new Date(date), dateFormat);

export const getLocalDateNoFormat = (date: string | Date): Date => {
  const tempDate = new Date(date);
  return addMinutes(tempDate, tempDate.getTimezoneOffset());
};

export const isoToLocal = (date: string, dateFormat = generalDate): string =>
  format(getLocalDateNoFormat(date), dateFormat);

export const normalizeDateString = (date: string | Date, endDay?: boolean): string => {
  let newDate = date;

  if (!date) {
    return '';
  }
  if (typeof date !== 'string') {
    newDate = new Date(date.setHours(12))?.toISOString();
  }

  return `${(newDate as string).split('T')[0]}T${endDay ? '23:59:00' : '00:00:00'}Z`;
};

/** Calendar date from `date`, time of day = current moment in UTC (`…Z`). */
export const normalizeDateStringWithCurrentUtcTime = (date: string | Date): string => {
  let newDate = date;

  if (!date) {
    return '';
  }
  if (typeof date !== 'string') {
    newDate = new Date(date.setHours(12))?.toISOString();
  }

  const datePart = (newDate as string).split('T')[0];
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const timePart = `${pad(now.getUTCHours())}:${pad(now.getUTCMinutes())}:${pad(
    now.getUTCSeconds(),
  )}`;

  return `${datePart}T${timePart}Z`;
};

export const setNoon = (date: string | Date): string => {
  let newDate = date;

  if (!date) {
    return '';
  }
  if (typeof date !== 'string') {
    newDate = new Date(date.setHours(12))?.toISOString();
  }

  return `${(newDate as string).split('T')[0]}T12:00:00Z`;
};

/**
 * True when “now” is on or after 00:00 of the calendar day after `endDate`
 * (using local start-of-day after `setNoon` normalization).
 */
export const isExpiredByEndDate = (endDate: string): boolean => {
  const endDay = startOfDay(new Date(setNoon(endDate)));
  return !isBefore(new Date(), addDays(endDay, 1));
};

export const destructHours = (hours: string): Date => {
  const date = new Date();
  const time = hours.split(':');
  date.setHours(Number(time[0]), Number(time[1]), Number(time[2]));

  return date;
};

export const getCombinedDateTime = (date: string, time?: string): Date =>
  time
    ? new Date(`${date?.split('T')[0]}T${new Date().toISOString()?.split('T')[1]}`)
    : new Date(`${date?.split('T')[0]}T${time?.split('T')[1]}`);

export const removeTime = (date?: string): string | undefined =>
  date ? date.split('T')?.[0] : undefined;

/**
 * Converts duration from "days.hours:minutes:seconds" or "hours:minutes:seconds" to "Xh Ym".
 * E.g. "23.19:26:45" -> "571h 26m" (23*24 + 19 hours), "19:26:45" -> "19h 26m"
 */
export const formatDurationToHours = (timeString: string): string => {
  if (!timeString?.trim()) return '0h 0m';

  const withDays = timeString.match(/^(\d+)\.(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);
  const withoutDays = timeString.match(/^(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d+))?$/);

  let totalHours: number;
  let minutes: number;

  if (withDays) {
    const days = parseInt(withDays[1], 10);
    const hours = parseInt(withDays[2], 10);
    minutes = parseInt(withDays[3], 10);
    totalHours = days * 24 + hours;
  } else if (withoutDays) {
    totalHours = parseInt(withoutDays[1], 10);
    minutes = parseInt(withoutDays[2], 10);
  } else {
    return '0h 0m';
  }

  return `${totalHours}h ${minutes}m`;
};
